/**
 * Structural checks on a `PricingInput`. The API parses requests with Zod in
 * `packages/contracts`; these checks guard the calculator itself (an app
 * preview, a backfill, a golden replay) against values that would make the
 * arithmetic meaningless: floats, negative prices, duplicate ids, bad dates.
 */
import { isBusinessDate } from '@qrkey/contracts/common';
import { BASIS_POINTS_MAX, BASIS_POINTS_WHOLE } from '@qrkey/contracts/money';
import { PRICE_CONTEXTS } from '@qrkey/contracts/org';
import { PricingError } from './errors.js';
import type { PricingInput } from './types.js';
import { TAX_COMPONENTS } from './types.js';

const ORDER_TYPES = new Set(['dine_in', 'takeaway', 'delivery', 'aggregator']);
const CHANNELS = new Set<string>(PRICE_CONTEXTS);
const SUPPLY_TYPES = new Set(['intra_state', 'inter_state']);
const ROUND_OFF_MODES = new Set(['none', 'nearest_rupee', 'up_rupee', 'down_rupee']);
const CHARGE_KINDS = new Set(['packaging', 'service', 'delivery', 'other']);

function fail(detail: string): never {
  throw new PricingError('INVALID_INPUT', detail);
}

function checkPaise(value: unknown, what: string): void {
  if (typeof value !== 'bigint') fail(`${what} must be a bigint number of paise`);
  if (value < 0n) fail(`${what} must not be negative`);
}

function checkRate(value: unknown, what: string, max: number): void {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value > max) {
    fail(`${what} must be an integer number of basis points in 0..${max}`);
  }
}

function checkQty(value: unknown, what: string): void {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    fail(`${what} must be a positive integer number of milli-units`);
  }
}

function checkUnique(ids: readonly string[], what: string): void {
  const seen = new Set<string>();
  for (const id of ids) {
    if (typeof id !== 'string' || id === '') fail(`${what} id must be a non-empty string`);
    if (seen.has(id)) throw new PricingError('DUPLICATE_ID', `${what} id ${id} appears twice`);
    seen.add(id);
  }
}

export function validateInput(input: PricingInput): void {
  const { context } = input;
  if (!ORDER_TYPES.has(context.orderType)) fail(`unknown order type ${context.orderType}`);
  if (!CHANNELS.has(context.channel)) fail(`unknown pricing channel ${context.channel}`);
  if (typeof context.source !== 'string' || context.source === '') {
    fail('source must be a non-empty string');
  }
  if (!SUPPLY_TYPES.has(context.supply)) fail(`unknown supply type ${context.supply}`);
  if (!isBusinessDate(context.taxDate)) fail(`taxDate ${context.taxDate} is not a YYYY-MM-DD date`);
  if (!ROUND_OFF_MODES.has(input.roundOff)) fail(`unknown round-off mode ${input.roundOff}`);

  checkUnique(
    input.taxGroups.map((g) => g.id),
    'tax group',
  );
  for (const group of input.taxGroups) {
    for (const rule of group.rules) {
      if (!TAX_COMPONENTS.includes(rule.component)) {
        fail(`tax group ${group.id}: unknown component ${rule.component}`);
      }
      checkRate(rule.rateBp, `tax group ${group.id} ${rule.component} rate`, BASIS_POINTS_MAX);
      if (!isBusinessDate(rule.effectiveFrom)) {
        fail(`tax group ${group.id}: effectiveFrom ${rule.effectiveFrom} is not a date`);
      }
      if (rule.effectiveTo !== null) {
        if (!isBusinessDate(rule.effectiveTo)) {
          fail(`tax group ${group.id}: effectiveTo ${rule.effectiveTo} is not a date`);
        }
        if (rule.effectiveTo <= rule.effectiveFrom) {
          fail(`tax group ${group.id}: effectiveTo must be after effectiveFrom`);
        }
      }
    }
  }

  checkUnique(
    input.lines.map((l) => l.id),
    'line',
  );
  for (const line of input.lines) {
    checkQty(line.qtyMilli, `line ${line.id} qtyMilli`);
    checkPaise(line.unitPricePaise, `line ${line.id} unitPricePaise`);
    if (typeof line.taxInclusive !== 'boolean') fail(`line ${line.id}: taxInclusive is required`);
    for (const [i, mod] of (line.modifiers ?? []).entries()) {
      checkPaise(mod.unitPricePaise, `line ${line.id} modifier ${i} unitPricePaise`);
      if (mod.qtyMilli !== undefined)
        checkQty(mod.qtyMilli, `line ${line.id} modifier ${i} qtyMilli`);
    }
  }

  const discounts = input.discounts ?? [];
  checkUnique(
    discounts.map((d) => d.id),
    'discount',
  );
  for (const d of discounts) {
    if (d.calc === 'percent') {
      checkRate(d.rateBp, `discount ${d.id} rate`, BASIS_POINTS_WHOLE);
      if (d.maxPaise !== undefined && d.maxPaise !== null) {
        checkPaise(d.maxPaise, `discount ${d.id} maxPaise`);
      }
    } else if (d.calc === 'flat') {
      checkPaise(d.amountPaise, `discount ${d.id} amountPaise`);
    } else {
      fail(`discount ${(d as { id: string }).id}: unknown calc`);
    }
    if (!['order', 'item', 'category'].includes(d.target.scope)) {
      fail(`discount ${d.id}: unknown scope`);
    }
  }

  const charges = input.charges ?? [];
  checkUnique(
    charges.map((c) => c.id),
    'charge',
  );
  for (const c of charges) {
    for (const channel of c.appliesTo?.channels ?? []) {
      if (!CHANNELS.has(channel)) fail(`charge ${c.id}: unknown pricing channel ${channel}`);
    }
    if (!CHARGE_KINDS.has(c.kind)) fail(`charge ${c.id}: unknown kind ${c.kind}`);
    if (c.calc === 'percent') {
      checkRate(c.rateBp, `charge ${c.id} rate`, BASIS_POINTS_MAX);
    } else if (c.calc === 'flat') {
      checkPaise(c.amountPaise, `charge ${c.id} amountPaise`);
    } else {
      fail(`charge ${(c as { id: string }).id}: unknown calc`);
    }
    if (typeof c.taxable !== 'boolean') fail(`charge ${c.id}: taxable is required`);
  }
}
